import React, { useState, useRef } from "react";
import { ChevronDown, Upload, Plus, Info } from "lucide-react";
import axios from "axios";
import AssignmentModal from "./AssignmentModal";
import QuizModal from "./Quiz";
import EditIcon from "../../Images/Edit_icon.svg";
import DeleteIcon from "../../Images/Delete_icon.svg";
import ToastMessage from "../../config/ToastMessage";
import { API_URL } from "../../config/api";

const initialFormData = {
  programName: "",
  programType: "",
  programLevel: "",
  duration: "",
  language: "",
  passingCondition: "80%",
  programPrice: "",
  maxClassSize: "10",
  minClassSize: "3",
};

const initialErrors = {
  programName: "",
  programType: "",
  programLevel: "",
  duration: "",
  language: "",
  passingCondition: "",
  programPrice: "",
  programBanner: "",
  programThumbnail: "",
  maxClassSize: "",
  minClassSize: "",
  modules: "",
  lessons: "",
};

export default function CreateProgramForm() {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState(initialErrors);

  const [modules, setModules] = useState([]);

  const [lessons, setLessons] = useState([]);

  const [newModule, setNewModule] = useState({ name: "", description: "" });
  const [selectedModule, setSelectedModule] = useState("");
  const [newLesson, setNewLesson] = useState("");
  const [selectedLessonTags, setSelectedLessonTags] = useState([]);
  const [showModuleForm, setShowModuleForm] = useState(false);
  const [showLessonForm, setShowLessonForm] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedLessonForAssignment, setSelectedLessonForAssignment] =
    useState(null);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [selectedLessonForQuiz, setSelectedLessonForQuiz] = useState(null);
  const [assignments, setAssignments] = useState({});
  const [editingModuleId, setEditingModuleId] = useState(null);
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [editModuleData, setEditModuleData] = useState({
    name: "",
    description: "",
  });
  const [editLessonData, setEditLessonData] = useState({ name: "", tags: [] });

  // Upload states
  const [programBanner, setProgramBanner] = useState("");
  const [programThumbnail, setProgramThumbnail] = useState("");
  const [videoTrailer, setVideoTrailer] = useState("");
  const [fileUploading, setFileUploading] = useState({
    banner: false,
    thumbnail: false,
    video: false,
  });
  const [uploadProgress, setUploadProgress] = useState({
    banner: 0,
    thumbnail: 0,
    video: 0,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState({ type: "", message: "" });

  // Ref to track uploaded files synchronously
  const uploadedFiles = useRef({
    banner: false,
    thumbnail: false,
    video: false,
  });

  // Cloudinary configuration
  const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const CLOUDINARY_UPLOAD_PRESET =
    process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

  const lessonTagOptions = [
    "Quiz",
    "Demo",
    "Lab",
    "Assignment",
    "Upload lesson Materials",
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const addModule = () => {
    if (newModule.name.trim()) {
      setModules((prev) => [
        ...prev,
        {
          id: prev.length + 1,
          name: newModule.name,
          description: newModule.description,
        },
      ]);
      setNewModule({ name: "", description: "" });
      setShowModuleForm(false);
    }
  };

  const addLesson = () => {
    // Validate module selection first
    if (!selectedModule) {
      setToast({
        type: "error",
        message: "Please select a module before adding a lesson.",
      });
      return;
    }

    // Validate lesson name
    if (!newLesson.trim()) {
      setToast({
        type: "error",
        message: "Please enter a lesson name.",
      });
      return;
    }

    // If validation passes, add the lesson
    setLessons((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        name: newLesson,
        moduleId: parseInt(selectedModule),
        tags: [...selectedLessonTags],
      },
    ]);

    setNewLesson("");
    setSelectedLessonTags([]);
    setShowLessonForm(false);
  };

  const toggleLessonTag = (tag) => {
    setSelectedLessonTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const handleCancel = () => {
    const confirmCancel = window.confirm(
      "Are you sure you want to cancel?\nAll unsaved changes will be lost.",
    );

    if (!confirmCancel) return;

    console.log("Cancel confirmed. Resetting entire form.");

    // Reset main form
    setFormData(initialFormData);
    setErrors(initialErrors);

    // Reset modules & lessons
    setModules([]);
    setLessons([]);

    // Reset module & lesson inputs
    setNewModule({ name: "", description: "" });
    setSelectedModule("");
    setNewLesson("");
    setSelectedLessonTags([]);

    // Reset UI states
    setShowModuleForm(false);
    setShowLessonForm(false);
    setShowAssignmentModal(false);
    setShowQuizModal(false);
    setSelectedLessonForAssignment(null);
    setSelectedLessonForQuiz(null);
    setEditingModuleId(null);
    setEditingLessonId(null);
    setEditModuleData({ name: "", description: "" });
    setEditLessonData({ name: "", tags: [] });

    // Reset uploads
    setProgramBanner("");
    setProgramThumbnail("");
    setVideoTrailer("");
    setFileUploading({
      banner: false,
      thumbnail: false,
      video: false,
    });
    setUploadProgress({
      banner: 0,
      thumbnail: 0,
      video: 0,
    });

    // Reset uploaded file tracking
    uploadedFiles.current = {
      banner: false,
      thumbnail: false,
      video: false,
    };

    // Clear toast
    setToast({ type: "", message: "" });
  };

  const handlePublish = async () => {
    // Validate required fields
    let newErrors = {};
    if (!formData.programName)
      newErrors.programName = "Program Name is required.";
    if (!formData.programType)
      newErrors.programType = "Program Type is required.";
    if (!formData.programLevel)
      newErrors.programLevel = "Program Level is required.";
    if (!formData.duration) newErrors.duration = "Duration is required.";
    if (!formData.language) newErrors.language = "Language is required.";
    if (!formData.passingCondition)
      newErrors.passingCondition = "Passing Condition is required.";
    if (!formData.programPrice)
      newErrors.programPrice = "Program Price is required.";
    if (!programBanner) newErrors.programBanner = "Program Banner is required.";
    if (!programThumbnail)
      newErrors.programThumbnail = "Program Thumbnail is required.";
    if (!formData.maxClassSize)
      newErrors.maxClassSize = "Maximum Class Size is required.";
    if (!formData.minClassSize)
      newErrors.minClassSize = "Minimum Class Size is required.";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setToast({
        type: "error",
        message: "Please fill all required fiels.",
      });
      return;
    }

    // Validate modules and lessons
    if (modules.length === 0) {
      setToast({
        type: "error",
        message: "Please add at least one module.",
      });
      return;
    }

    if (lessons.length === 0) {
      setToast({
        type: "error",
        message: "Please add at least one lesson.",
      });
      return;
    }

    setIsSaving(true);
    setToast({ type: "", message: "" });

    try {
      // Map form data to API payload with correct backend format
      const languageMap = {
        english: "en",
        spanish: "es",
        french: "fr",
      };

      const courseTypeMap = {
        "in-person": "inperson",
        online: "online",
        hybrid: "hybrid",
      };

      const durationMap = {
        "5-days": "five_days",
        "10-days": "ten_days",
        "15-days": "fifteen_days",
        "1-month": "one_month",
        others: "others",
      };

      const coursePayload = {
        title: formData.programName,
        description: "", // Add description field if needed
        amount: parseFloat(formData.programPrice),
        level: formData.programLevel.toUpperCase(),
        language:
          languageMap[formData.language.toLowerCase()] ||
          formData.language.toLowerCase(),
        course_type:
          courseTypeMap[formData.programType] ||
          formData.programType.toLowerCase(),
        min_class_size: parseInt(formData.minClassSize),
        max_class_size: parseInt(formData.maxClassSize),
        video_trailer: videoTrailer || null,
        program_banner: programBanner,
        program_thumbnail: programThumbnail,
        passing_score: parseFloat(formData.passingCondition.replace("%", "")),
        duration: durationMap[formData.duration] || formData.duration,
        is_published: true,
        modules: modules.map((module, moduleIndex) => ({
          title: module.name,
          position: moduleIndex + 1,
          lessons: lessons
            .filter((lesson) => lesson.moduleId === module.id)
            .map((lesson, lessonIndex) => ({
              title: lesson.name,
              content: "", // Add content field if needed
              lesson_type: "VIDEO", // Default to VIDEO, can be enhanced later
              assessment_list: lesson.tags.map((tag) =>
                tag.replace(/\s+/g, "_"),
              ),
              video_url: null, // Add video URL field if needed
              duration_seconds: null, // Add duration field if needed
              position: lessonIndex + 1,
            })),
        })),
      };
      console.log("course creation Payload:", coursePayload);

      // Call create course API
      const response = await axios.post(
        `${API_URL}/courses/create_course`,
        coursePayload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`, // Add auth header if needed
            "Content-Type": "application/json",
          },
        },
      );

      setToast({
        type: "success",
        message: "Program published successfully!",
      });

      // Refresh the page after successful publish
      setTimeout(() => {
        window.location.reload();
      }, 2000);

      // Reset form or redirect to course list
      console.log("Course created:", response.data);
    } catch (error) {
      console.error("Error publishing course:", error);
      setToast({
        type: "error",
        message:
          error.response?.data?.detail ||
          "Failed to publish program. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLessonTagClick = (tag) => {
    toggleLessonTag(tag);
  };

  const handleEditModule = (moduleId) => {
    const module = modules.find((m) => m.id === moduleId);
    setEditModuleData({ name: module.name, description: module.description });
    setEditingModuleId(moduleId);
  };

  const handleSaveModuleEdit = () => {
    setModules((prev) =>
      prev.map((m) =>
        m.id === editingModuleId ? { ...m, ...editModuleData } : m,
      ),
    );
    setEditingModuleId(null);
    setEditModuleData({ name: "", description: "" });
  };

  const handleCancelModuleEdit = () => {
    setEditingModuleId(null);
    setEditModuleData({ name: "", description: "" });
  };

  const handleDeleteModule = (moduleId) => {
    if (window.confirm("Are you sure you want to delete this module?")) {
      setModules((prev) => prev.filter((m) => m.id !== moduleId));
      setLessons((prev) => prev.filter((l) => l.moduleId !== moduleId));
    }
  };

  const handleEditLesson = (lessonId) => {
    const lesson = lessons.find((l) => l.id === lessonId);
    setEditLessonData({ name: lesson.name, tags: [...lesson.tags] });
    setEditingLessonId(lessonId);
  };

  const handleSaveLessonEdit = () => {
    setLessons((prev) =>
      prev.map((l) =>
        l.id === editingLessonId ? { ...l, ...editLessonData } : l,
      ),
    );
    setEditingLessonId(null);
    setEditLessonData({ name: "", tags: [] });
  };

  const handleCancelLessonEdit = () => {
    setEditingLessonId(null);
    setEditLessonData({ name: "", tags: [] });
  };

  const handleDeleteLesson = (lessonId) => {
    if (window.confirm("Are you sure you want to delete this lesson?")) {
      setLessons((prev) => prev.filter((l) => l.id !== lessonId));
    }
  };

  const toggleEditLessonTag = (tag) => {
    setEditLessonData((prev) => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter((t) => t !== tag)
        : [...prev.tags, tag],
    }));
  };

  // Cloudinary upload handler
  const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (50MB for video, 10MB for images)
    const maxSize = type === "video" ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setToast({
        type: "error",
        message: `File size exceeds ${maxSize / (1024 * 1024)}MB limit`,
      });
      return;
    }

    setFileUploading((prev) => ({ ...prev, [type]: true }));
    setToast({ type: "", message: "" });
    setUploadProgress((prev) => ({ ...prev, [type]: 0 }));

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);

    try {
      const response = await axios.post(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/${
          type === "video" ? "video" : "image"
        }/upload`,
        formData,
        {
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 100),
            );
            setUploadProgress((prev) => ({
              ...prev,
              [type]: percentCompleted,
            }));
          },
        },
      );

      const url = response.data.secure_url;

      // Update the appropriate state
      if (type === "banner") {
        setProgramBanner(url);
        setErrors((prev) => ({ ...prev, programBanner: "" }));
      } else if (type === "thumbnail") {
        setProgramThumbnail(url);
        setErrors((prev) => ({ ...prev, programThumbnail: "" }));
      } else if (type === "video") {
        setVideoTrailer(url);
      }

      // Mark file as uploaded in ref
      uploadedFiles.current[type] = true;

      setToast({
        type: "success",
        message: `${
          type.charAt(0).toUpperCase() + type.slice(1)
        } uploaded successfully!`,
      });
    } catch (error) {
      console.error("Upload error:", error);
      setToast({ type: "error", message: "Upload failed. Please try again." });
    } finally {
      setFileUploading((prev) => ({ ...prev, [type]: false }));
      // Don't reset progress here - keep it at 100% until next upload
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl">
        <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Create Program
            </h1>
            <p className="text-sm text-gray-600">
              Complete the form and publish your Program instantly
            </p>
          </div>
          {/* Program Info Section */}
          <h2 className="text-xl font-bold text-gray-900 mb-6">Program Info</h2>

          {/* Row 1 */}
          <div className="grid grid-cols-3 gap-6 mb-6">
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
                Program Name
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter Program Name"
                value={formData.programName}
                onChange={(e) =>
                  handleInputChange("programName", e.target.value)
                }
                className="h-10 px-3 bg-white rounded border border-gray-300 text-sm outline-none focus:border-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5]"
              />
              {errors.programName && (
                <p className="text-red-500 text-sm">{errors.programName}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
                Program Type
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={formData.programType}
                  onChange={(e) =>
                    handleInputChange("programType", e.target.value)
                  }
                  className="w-full h-10 px-3 bg-white rounded border border-gray-300 text-sm appearance-none outline-none focus:border-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5] text-gray-500"
                >
                  <option value="">Select Program Type</option>
                  <option value="in-person">In person/ Regular</option>
                  <option value="virtual">Online</option>
                </select>
                <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
              {errors.programType && (
                <p className="text-red-500 text-sm">{errors.programType}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
                Program Level
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={formData.programLevel}
                  onChange={(e) =>
                    handleInputChange("programLevel", e.target.value)
                  }
                  className="w-full h-10 px-3 bg-white rounded border border-gray-300 text-sm appearance-none outline-none focus:border-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5] text-gray-500"
                >
                  <option value="">Select Level</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
                <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
              {errors.programLevel && (
                <p className="text-red-500 text-sm">{errors.programLevel}</p>
              )}
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-3 gap-6 mb-6">
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
                Duration
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={formData.duration}
                  onChange={(e) =>
                    handleInputChange("duration", e.target.value)
                  }
                  className="w-full h-10 px-3 bg-white rounded border border-gray-300 text-sm appearance-none outline-none focus:border-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5] text-gray-500"
                >
                  <option value="">Select Duration</option>
                  <option value="5-days">5 Days</option>
                  <option value="10-days">10 Days</option>
                  <option value="15-days">15 Days</option>
                  <option value="1-month">1 Month</option>
                  <option value="others">Others</option>
                </select>
                <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
              {errors.duration && (
                <p className="text-red-500 text-sm">{errors.duration}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
                Language
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={formData.language}
                  onChange={(e) =>
                    handleInputChange("language", e.target.value)
                  }
                  className="w-full h-10 px-3 bg-white rounded border border-gray-300 text-sm appearance-none outline-none focus:border-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5] text-gray-500"
                >
                  <option value="">Select Language</option>
                  <option value="english">English</option>
                  <option value="spanish">Spanish</option>
                  <option value="french">French</option>
                </select>
                <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
              {errors.language && (
                <p className="text-red-500 text-sm">{errors.language}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
                Success Criteria (Passing Score)
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={formData.passingCondition}
                  onChange={(e) =>
                    handleInputChange("passingCondition", e.target.value)
                  }
                  className="w-full h-10 px-3 bg-white rounded border border-gray-300 text-sm appearance-none outline-none focus:border-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5] text-gray-500"
                >
                  <option value="80%">80%</option>
                  <option value="70%">70%</option>
                  <option value="75%">75%</option>
                  <option value="85%">85%</option>
                  <option value="90%">90%</option>
                </select>
                <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
              {errors.passingCondition && (
                <p className="text-red-500 text-sm">
                  {errors.passingCondition}
                </p>
              )}
            </div>
          </div>

          {/* Row 3 - Upload Fields */}
          <div className="grid grid-cols-3 gap-6">
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
                Program Banner
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, "banner")}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  disabled={fileUploading.banner}
                />
                <div
                  className={`h-32 border-2 border-dashed rounded bg-gray-50 flex flex-col justify-center items-center gap-2 cursor-pointer hover:border-[#4AACC5] transition ${
                    programBanner
                      ? "border-green-500 bg-green-50"
                      : "border-gray-300"
                  }`}
                >
                  {fileUploading.banner ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-8 h-8 border-4 border-[#4AACC5] border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-sm text-gray-600">
                        Uploading... {uploadProgress.banner}%
                      </span>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-[#4AACC5] h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress.banner}%` }}
                        ></div>
                      </div>
                    </div>
                  ) : programBanner ? (
                    <img
                      src={programBanner}
                      alt="Banner"
                      className="w-full h-full object-cover rounded"
                    />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-gray-400" />
                      <span className="text-sm text-gray-500">
                        Upload Image
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-1 text-xs text-gray-500">
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>Size: 1440 x 460 pixels, Files: JPG, JPEG, PNG</span>
              </div>
              {errors.programBanner && (
                <p className="text-red-500 text-sm">{errors.programBanner}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
                Program Thumbnail
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, "thumbnail")}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  disabled={fileUploading.thumbnail}
                />
                <div
                  className={`h-32 border-2 border-dashed rounded bg-gray-50 flex flex-col justify-center items-center gap-2 cursor-pointer hover:border-[#4AACC5] transition ${
                    programThumbnail
                      ? "border-green-500 bg-green-50"
                      : "border-gray-300"
                  }`}
                >
                  {fileUploading.thumbnail ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-8 h-8 border-4 border-[#4AACC5] border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-sm text-gray-600">
                        Uploading... {uploadProgress.thumbnail}%
                      </span>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-[#4AACC5] h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress.thumbnail}%` }}
                        ></div>
                      </div>
                    </div>
                  ) : programThumbnail ? (
                    <img
                      src={programThumbnail}
                      alt="Thumbnail"
                      className="w-full h-full object-cover rounded"
                    />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-gray-400" />
                      <span className="text-sm text-gray-500">
                        Upload Image
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-1 text-xs text-gray-500">
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>Size: 700 x 430 pixels, Files: JPG, JPEG, PNG</span>
              </div>
              {errors.programThumbnail && (
                <p className="text-red-500 text-sm">
                  {errors.programThumbnail}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Video Trailer (optional)
              </label>

              {!videoTrailer ? (
                <div className="relative">
                  <input
                    type="file"
                    accept="video/*"
                    onChange={(e) => handleFileUpload(e, "video")}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    disabled={fileUploading.video}
                  />
                  <div className="h-32 border-2 border-dashed rounded border-gray-300 bg-gray-50 flex flex-col justify-center items-center gap-2 cursor-pointer hover:border-[#4AACC5] transition">
                    {fileUploading.video ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-8 h-8 border-4 border-[#4AACC5] border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-sm text-gray-600">
                          Uploading... {uploadProgress.video}%
                        </span>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-[#4AACC5] h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress.video}%` }}
                          ></div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-gray-400" />
                        <span className="text-sm text-gray-500">
                          Upload Video
                        </span>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="relative h-32 border-2 border-dashed rounded border-green-500 bg-green-50">
                  <video
                    src={videoTrailer}
                    className="w-full h-full object-cover rounded"
                    controls
                    controlsList="nodownload"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setVideoTrailer("");
                      uploadedFiles.current.video = false;
                    }}
                    className="absolute -top-2 -right-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-semibold hover:bg-red-600 shadow-lg"
                  >
                    ✕ Remove
                  </button>
                </div>
              )}

              <div className="flex items-start gap-1 text-xs text-gray-500">
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>30–60 seconds and 20 MB – 50 MB</span>
              </div>
            </div>
          </div>
        </div>
        {/* Add Module Section */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">Add Module</h2>
            <button
              onClick={() => setShowModuleForm(!showModuleForm)}
              className="px-4 py-2 bg-[#4AACC5] text-white rounded text-sm font-semibold flex items-center gap-2 hover:bg-[#3d96ad] transition"
            >
              <Plus className="w-4 h-4" />
              Add Module
            </button>
          </div>

          {showModuleForm && (
            <div className="grid grid-cols-2 gap-6 mb-6 p-4 bg-gray-50 rounded border border-gray-200">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Module Name
                </label>
                <input
                  type="text"
                  placeholder="Enter Module Name"
                  value={newModule.name}
                  onChange={(e) =>
                    setNewModule((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="h-10 px-3 bg-white rounded border border-gray-300 text-sm outline-none focus:border-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5]"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Module Description
                </label>
                <textarea
                  placeholder="Write Text.."
                  value={newModule.description}
                  onChange={(e) =>
                    setNewModule((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  className="h-24 px-3 py-2 bg-white rounded border border-gray-300 text-sm resize-none outline-none focus:border-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5]"
                />
              </div>

              <div className="col-span-2 flex gap-2 justify-end">
                <button
                  onClick={() => {
                    setShowModuleForm(false);
                    setNewModule({ name: "", description: "" });
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded text-sm font-semibold hover:bg-gray-300 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={addModule}
                  className="px-4 py-2 bg-[#4AACC5] text-white rounded text-sm font-semibold hover:bg-[#3d96ad] transition"
                >
                  Save Module
                </button>
              </div>
            </div>
          )}

          {/* Module List */}
          <div className="space-y-3">
            {modules.map((module) => (
              <div
                key={module.id}
                className="p-4 bg-gray-50 rounded border border-gray-200"
              >
                {editingModuleId === module.id ? (
                  <div className="grid grid-cols-2 gap-6">
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-gray-700">
                        Module Name
                      </label>
                      <input
                        type="text"
                        placeholder="Enter Module Name"
                        value={editModuleData.name}
                        onChange={(e) =>
                          setEditModuleData((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        className="h-10 px-3 bg-white rounded border border-gray-300 text-sm outline-none focus:border-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5]"
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-gray-700">
                        Module Description
                      </label>
                      <textarea
                        placeholder="Write Text.."
                        value={editModuleData.description}
                        onChange={(e) =>
                          setEditModuleData((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                        className="h-24 px-3 py-2 bg-white rounded border border-gray-300 text-sm resize-none outline-none focus:border-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5]"
                      />
                    </div>

                    <div className="col-span-2 flex gap-2 justify-end">
                      <button
                        onClick={handleCancelModuleEdit}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded text-sm font-semibold hover:bg-gray-300 transition"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveModuleEdit}
                        className="px-4 py-2 bg-[#4AACC5] text-white rounded text-sm font-semibold hover:bg-[#3d96ad] transition"
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-2 flex-1">
                      <span className="text-base font-semibold text-[#4AACC5]">
                        Module {module.id} :
                      </span>
                      <span className="text-base font-semibold text-gray-900">
                        {" "}
                        {module.name}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditModule(module.id)}
                        className="p-1 hover:bg-gray-200 rounded transition"
                        title="Edit Module"
                      >
                        <img src={EditIcon} alt="Edit" className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteModule(module.id)}
                        className="p-1 hover:bg-gray-200 rounded transition"
                        title="Delete Module"
                      >
                        <img
                          src={DeleteIcon}
                          alt="Delete"
                          className="w-4 h-4"
                        />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Add Lesson Section */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Add Lesson</h2>

          <div className="flex flex-col gap-2 mb-4 max-w-sm">
            <label className="text-sm font-medium text-gray-700">Module</label>
            <div className="relative">
              <select
                value={selectedModule}
                onChange={(e) => setSelectedModule(e.target.value)}
                className="w-full h-10 px-3 bg-white rounded border border-gray-300 text-sm appearance-none outline-none focus:border-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5] text-gray-500"
              >
                <option value="">Select Module</option>
                {modules.map((module) => (
                  <option key={module.id} value={module.id}>
                    Module {module.id}: {module.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {!showLessonForm && (
            <button
              onClick={() => setShowLessonForm(true)}
              className="px-4 py-2 bg-[#4AACC5] text-white rounded text-sm font-semibold flex items-center gap-2 hover:bg-[#3d96ad] transition mb-6"
            >
              <Plus className="w-4 h-4" />
              Add Lesson
            </button>
          )}

          {showLessonForm && (
            <div className="p-4 bg-gray-50 rounded border border-gray-200 mb-6">
              <input
                type="text"
                placeholder="Enter Lesson Name"
                value={newLesson}
                onChange={(e) => setNewLesson(e.target.value)}
                className="w-full h-10 px-3 bg-white rounded border border-gray-300 text-sm outline-none focus:border-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5] mb-4"
              />

              <div className="flex justify-between items-start">
                <div className="flex flex-wrap gap-3">
                  {lessonTagOptions.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => handleLessonTagClick(tag)}
                      className={`px-3 py-1.5 rounded text-sm font-medium flex items-center gap-2 border transition ${
                        selectedLessonTags.includes(tag)
                          ? "bg-green-100 border-green-500 text-green-800 shadow-sm"
                          : "bg-white border-gray-300"
                      }`}
                    >
                      <Plus className="w-3 h-3" />
                      {tag}
                    </button>
                  ))}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowLessonForm(false);
                      setNewLesson("");
                      setSelectedLessonTags([]);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded text-sm font-semibold hover:bg-gray-300 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addLesson}
                    className="px-4 py-2 bg-[#4AACC5] text-white rounded text-sm font-semibold hover:bg-[#3d96ad] transition"
                  >
                    Save Lesson
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Lesson List */}
          <div className="space-y-6">
            {modules.map((module) => (
              <div
                key={module.id}
                className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm"
              >
                {/* ✅ Module Title */}
                <h3 className="flex items-start text-lg font-bold text-[#4AACC5] mb-3">
                  Module {module.id}: {module.name}
                </h3>

                {/* ✅ Lessons under this Module */}
                <div className="space-y-3">
                  {lessons.filter((l) => l.moduleId === module.id).length ===
                  0 ? (
                    <p className="text-sm text-gray-400">
                      No lessons added yet
                    </p>
                  ) : (
                    lessons
                      .filter((l) => l.moduleId === module.id)
                      .map((lesson, index) => (
                        <div
                          key={lesson.id}
                          className="p-3 bg-gray-50 rounded border border-gray-200"
                        >
                          {editingLessonId === lesson.id ? (
                            <div className="space-y-4">
                              <div className="flex flex-col gap-2">
                                <label className="text-sm font-medium text-gray-700">
                                  Lesson Name
                                </label>
                                <input
                                  type="text"
                                  placeholder="Enter Lesson Name"
                                  value={editLessonData.name}
                                  onChange={(e) =>
                                    setEditLessonData((prev) => ({
                                      ...prev,
                                      name: e.target.value,
                                    }))
                                  }
                                  className="h-10 px-3 bg-white rounded border border-gray-300 text-sm outline-none focus:border-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5]"
                                />
                              </div>

                              <div className="flex flex-col gap-2">
                                <label className="text-sm font-medium text-gray-700">
                                  Tags
                                </label>
                                <div className="flex flex-wrap gap-3">
                                  {lessonTagOptions.map((tag) => (
                                    <button
                                      key={tag}
                                      onClick={() => toggleEditLessonTag(tag)}
                                      className={`px-3 py-1.5 rounded text-sm font-medium flex items-center gap-2 border transition ${
                                        editLessonData.tags.includes(tag)
                                          ? "bg-green-100 border-green-500 text-green-800 shadow-sm"
                                          : "bg-white border-gray-300"
                                      }`}
                                    >
                                      <Plus className="w-3 h-3" />
                                      {tag}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              <div className="flex gap-2 justify-end">
                                <button
                                  onClick={handleCancelLessonEdit}
                                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded text-sm font-semibold hover:bg-gray-300 transition"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={handleSaveLessonEdit}
                                  className="px-4 py-2 bg-[#4AACC5] text-white rounded text-sm font-semibold hover:bg-[#3d96ad] transition"
                                >
                                  Save Changes
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-2 flex-1">
                                  <span className="font-semibold text-[#4AACC5]">
                                    Lesson {index + 1}:
                                  </span>
                                  <span className="font-medium text-gray-900">
                                    {lesson.name}
                                  </span>
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleEditLesson(lesson.id)}
                                    className="p-1 hover:bg-gray-200 rounded transition"
                                    title="Edit Lesson"
                                  >
                                    <img
                                      src={EditIcon}
                                      alt="Edit"
                                      className="w-4 h-4"
                                    />
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleDeleteLesson(lesson.id)
                                    }
                                    className="p-1 hover:bg-gray-200 rounded transition"
                                    title="Delete Lesson"
                                  >
                                    <img
                                      src={DeleteIcon}
                                      alt="Delete"
                                      className="w-4 h-4"
                                    />
                                  </button>
                                </div>
                              </div>

                              <div className="flex flex-wrap gap-2 mt-2">
                                {lesson.tags.map((tag) => (
                                  <span
                                    key={tag}
                                    className="px-3 py-1 bg-white rounded text-xs font-medium text-gray-700 border"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      ))
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing Section */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
          <div className="grid grid-cols-3 gap-6">
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
                Program Price
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter Program Price"
                value={formData.programPrice}
                onChange={(e) =>
                  handleInputChange("programPrice", e.target.value)
                }
                className="h-10 px-3 bg-white rounded border border-gray-300 text-sm outline-none focus:border-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5]"
              />
              {errors.programPrice && (
                <p className="text-red-500 text-sm">{errors.programPrice}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
                Maximum Class Size
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Maximum student 10"
                value={formData.maxClassSize}
                onChange={(e) =>
                  handleInputChange("maxClassSize", e.target.value)
                }
                className="h-10 px-3 bg-white rounded border border-gray-300 text-sm outline-none focus:border-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5]"
              />
              {errors.maxClassSize && (
                <p className="text-red-500 text-sm">{errors.maxClassSize}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
                Minimum Class Size
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Minimum student 3"
                value={formData.minClassSize}
                onChange={(e) =>
                  handleInputChange("minClassSize", e.target.value)
                }
                className="h-10 px-3 bg-white rounded border border-gray-300 text-sm outline-none focus:border-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5]"
              />
              {errors.minClassSize && (
                <p className="text-red-500 text-sm">{errors.minClassSize}</p>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={handleCancel}
            className="flex-1 max-w-xs px-6 py-3 bg-[#4AACC5] text-white rounded text-base font-semibold hover:bg-[#3d96ad] transition"
          >
            Cancel
          </button>
          <button
            onClick={handlePublish}
            className="flex-1 max-w-xs px-6 py-3 bg-[#4AACC5] text-white rounded text-base font-semibold hover:bg-[#3d96ad] transition disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={
              isSaving ||
              fileUploading.banner ||
              fileUploading.thumbnail ||
              fileUploading.video
            }
          >
            {isSaving ? "Saving..." : "Save Course"}
          </button>
        </div>

        {/* Footer */}
        <div className="w-full h-14 bg-[#4AACC5] flex items-center justify-center rounded">
          <p className="text-white text-sm font-medium">
            © 2025 SkillsMD | All rights reserved
          </p>
        </div>
      </div>
      <ToastMessage
        type={toast.type}
        message={toast.message}
        onClose={() => setToast({ type: "", message: "" })}
      />
    </div>
  );
}
